import { format, formatDistanceToNow } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useAdminApiKeys } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import Tooltip from '@dashboard/components/Tooltip';
import { Badge } from '@dashboard/components/ui/badge';

const PERMISSION_OPTIONS = [
  'admin:read',
  'admin:write',
  'account:read',
  'tickets:read',
  'tickets:write',
  'customers:read',
  'customers:write',
  'webhooks:read',
  'webhooks:write',
  'payments:read',
  'payments:write',
  'subscriptions:read',
  'subscriptions:write',
  'bookings:read',
  'bookings:write',
  'coupons:read',
  'coupons:write',
  'invoices:read',
  'invoices:write',
  'pages:read',
  'pages:write',
  'products:read',
  'products:write',
  'submissions:read',
  'submissions:write',
];

type ApiKeyRow = {
  _id: string;
  name: string;
  keyPreview: string;
  permissions: string[];
  scopes: string[];
  lastUsedAt?: string | null;
  usageCount: number;
  isDeleted?: boolean;
  isOAuth?: boolean;
  expiresAt?: string | null;
  createdAt: string;
  seller?: { _id: string; displayName?: string; name?: string } | null;
};

const ApiKeys = () => {
  const history = useHistory();

  const query = useUrlQuery();
  const { search, searchInput, setSearchInput } = useSearchInput(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [permission, setPermission] = useState(query.permission || '');
  const [status, setStatus] = useState(query.status || '');
  const [isOAuth, setIsOAuth] = useState(query.isOAuth || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        ...(permission && { permission }),
        ...(status && { status }),
        ...(isOAuth && { isOAuth }),
      }),
    });
  }, [sortBy, sortDirection, search, permission, status, isOAuth]);

  const filters = {
    sortBy,
    sortDirection,
    search,
    permission,
    status,
    isOAuth,
    limit: 20,
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useAdminApiKeys(filters);

  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  const handleSortingChange = (updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  const flatData: ApiKeyRow[] = useMemo(
    () => data?.pages?.flatMap((page: any) => page.data) ?? [],
    [data],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Name" />,
        cell: ({ row }: any) => <span>{row.original.name}</span>,
        enableSorting: true,
      },
      {
        accessorKey: 'keyPreview',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Key preview" />,
        cell: ({ row }: any) => (
          <code className="text-xs bg-zinc-100 dark:bg-muted px-1.5 py-0.5 rounded">
            {row.original.keyPreview}…
          </code>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'seller.displayName',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Seller" />,
        cell: ({ row }: any) => {
          const apiKey = row.original;
          if (!apiKey.seller) {
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          }
          return (
            <Link href={`/admin/merchants/${apiKey.seller._id}`}>
              {apiKey.seller.displayName || apiKey.seller.name}
            </Link>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'permissions',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Permissions" />,
        cell: ({ row }: any) => {
          const perms: string[] = row.original.permissions || [];
          if (perms.length === 0) {
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          }
          return (
            <Tooltip label={perms.join(', ')} side="bottom">
              <Badge variant="outline" className="cursor-help">
                {perms.length} {perms.length === 1 ? 'permission' : 'permissions'}
              </Badge>
            </Tooltip>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'usageCount',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Usage" />,
        cell: ({ row }: any) => {
          const count: number = row.original.usageCount || 0;
          return (
            <span className={count === 0 ? 'text-zinc-400 dark:text-muted-foreground' : ''}>
              {count === 0 ? '—' : count.toLocaleString()}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'lastUsedAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Last used" />,
        cell: ({ row }: any) => {
          const lastUsedAt = row.original.lastUsedAt;
          if (!lastUsedAt)
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          const date = new Date(lastUsedAt);
          return (
            <Tooltip label={format(date, 'dd MMM yyyy HH:mm')} side="bottom">
              <span className="cursor-help">{formatDistanceToNow(date, { addSuffix: true })}</span>
            </Tooltip>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Created" />,
        cell: ({ row }: any) => {
          const createdAt = row.original.createdAt;
          return createdAt ? format(new Date(createdAt), 'dd MMM yyyy HH:mm') : '';
        },
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }: any) => {
          const apiKey = row.original;
          if (apiKey.isDeleted) {
            return <Badge variant="destructive">Deleted</Badge>;
          }
          if (apiKey.expiresAt && new Date(apiKey.expiresAt) < new Date()) {
            return <Badge variant="outline">Expired</Badge>;
          }
          if (apiKey.isOAuth) {
            return <Badge variant="outline">OAuth</Badge>;
          }
          return <Badge variant="secondary">Active</Badge>;
        },
        enableSorting: false,
      },
    ],
    [],
  );

  const total = data?.pages[0]?.total;

  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto flex h-9 items-center text-sm text-gray-500 dark:text-muted-foreground">
        {total} keys
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select
          name="status"
          value={status || 'all'}
          onValueChange={value => setStatus(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="permission">Permission</Label>
        <Select
          name="permission"
          value={permission || 'all'}
          onValueChange={value => setPermission(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {PERMISSION_OPTIONS.map(p => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="isOAuth">OAuth</Label>
        <Select
          name="isOAuth"
          value={isOAuth || 'all'}
          onValueChange={value => setIsOAuth(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={(e: any) => setSearchInput(e.target.value)}
              placeholder="Search by name or key preview"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>API keys</h1>
        {filterControls}
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No API keys found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default ApiKeys;
